import html2pdf from 'html2pdf.js';

document.addEventListener('DOMContentLoaded', function () {
  const pc2b = document.querySelector('.page-template-template-pc2b');
  const singleProduct = document.querySelector('.single-product');
  if ((pc2b || singleProduct) && typeof PC2BData !== 'undefined') {
    console.log(PC2BData);
    let selectedProductType = null;
    let selectedDecorationIndex = null;
    let blankProducts = [];
    let addedPositions = [];
    let eventsBound = false;
    let productTypeEventsBound = false;

    try {
      bindEvents();
      if (singleProduct) {
        blankProducts = createBlankEntries();
        handleProductTypeChange('SUPPLIED_PRODUCTS');
      }
    } catch (error) {
      console.error('Error initializing app:', error);

      const root = document.getElementById('positions-root');
      if (root) {
        root.innerHTML = `
            <div class="text-center py-12 px-4 bg-red-50 rounded-lg border border-red-200">
              <p class="text-red-600 font-semibold mb-2">Error loading application</p>
              <p class="text-sm text-red-500">Please refresh the page. If the problem persists, check the browser console.</p>
            </div>
          `;
      }
    }

    function calculate() {
      const gstRate = 0.1;
      let positionsTotal = 0;
      let setupTotal = 0;
      let servicesTotal = 0;

      const positionBreakdowns = addedPositions.map((position) => {
        const breakdown = calculatePositionBreakdown(position);
        return {
          id: position.id,
          breakdown: breakdown,
        };
      });

      positionBreakdowns.forEach(({ breakdown }) => {
        positionsTotal += breakdown?.subtotal || 0;
        setupTotal += breakdown?.setup || 0;
        servicesTotal += breakdown?.services || 0;
      });

      // PC2A only: unbranded/blank products are separate line items, each with
      // its own size/quantity (independent of decoration qty), summed once.
      const blanks = singleProduct ? calculateBlankProducts() : [];
      const blankTotal = blanks.reduce((sum, b) => sum + (b.total || 0), 0);

      const subtotalBeforeGst = positionsTotal + blankTotal;
      const gst = subtotalBeforeGst * gstRate;
      const grandTotal = subtotalBeforeGst + gst;

      return {
        positions: positionBreakdowns,
        blanks: blanks,
        summary: {
          positionsTotal: positionsTotal,
          blankTotal: blankTotal,
          setupTotal: setupTotal,
          servicesTotal: servicesTotal,
          gst: gst,
          grandTotal: grandTotal,
        },
      };
    }

    // PC2A only: helpers for the standalone unbranded/blank products.
    function getBlankPricingArray(size) {
      if (typeof pc2UnbrandedPrice === 'undefined' || !pc2UnbrandedPrice) {
        return [];
      }
      if (activeSizePricing) {
        const key = size || Object.keys(pc2UnbrandedPrice)[0];
        return pc2UnbrandedPrice[key] || [];
      }
      return pc2UnbrandedPrice[0] || [];
    }

    function getBlankMoq(size) {
      const qtys = getBlankPricingArray(size)
        .map((p) => parseInt(p.qty, 10))
        .filter((q) => !Number.isNaN(q));
      return qtys.length ? Math.min(...qtys) : 1;
    }

    function createBlankEntries() {
      // One fixed row per size (qty defaults to 0; only filled rows are charged).
      if (activeSizePricing && typeof pc2UnbrandedPrice !== 'undefined') {
        return Object.keys(pc2UnbrandedPrice).map((size) => ({
          id: size,
          size: size,
          qty: 0,
        }));
      }
      return [{ id: 'blank-single', size: null, qty: 0 }];
    }

    function calculateBlankProducts() {
      return blankProducts
        .filter((entry) => (entry.qty || 0) > 0)
        .map((entry) => {
          const pricing = getBlankPricingArray(entry.size);
          const qty = entry.qty || 0;
          const size = activeSizePricing
            ? entry.size || Object.keys(pc2UnbrandedPrice)[0]
            : null;
          if (!pricing.length) {
            return { id: entry.id, size: size, qty: qty, unit: 0, total: 0 };
          }
          const moq = getBlankMoq(entry.size);
          let unit = findProductPriceByQty(qty, pricing) || 0;
          // If quantity is below MOQ, apply MOQ logic 1 to unbranded pricing.
          if (qty > 0 && qty < moq) {
            const priceAtMoq = findProductPriceByQty(moq, pricing) || 0;
            unit = (moq * priceAtMoq) / qty;
          }
          return {
            id: entry.id,
            size: size,
            qty: qty,
            unit: round2(unit),
            total: round2(unit * qty),
          };
        });
    }

    function calculatePositionBreakdown(position) {
      const {
        decorationId,
        type,
        qty,
        moqQty,
        numColors,
        setupType,
        hasSpecialMaterial,
        multiplierLimit,
        specialMaterialType,
        services,
        size,
      } = position;
      const decoration = getDecorationDataById(decorationId);

      if (type === 'Embroidery') {
        const stitches = position.stitches || 1;
        let unit = findEmbroideryUnitPrice(
          qty || 0,
          stitches,
          decoration.prices,
        );
        if (qty < moqQty && decoration.moqLogic === 'Logic 1') {
          const price1 = findEmbroideryUnitPrice(
            moqQty || 0,
            stitches,
            decoration.prices,
          );
          unit = (moqQty * price1) / qty;
        }

        if (hasSpecialMaterial && specialMaterialType.length > 0) {
          let specialMaterialPercentage = 0;
          specialMaterialType.forEach((smType) => {
            const sm = decoration?.specialMaterial.find(
              (sm) => sm.name === smType,
            );
            if (sm) {
              specialMaterialPercentage += sm.price;
            }
          });
          specialMaterialPercentage =
            specialMaterialPercentage > multiplierLimit
              ? multiplierLimit
              : specialMaterialPercentage;

          unit = unit * ((100 + specialMaterialPercentage) / 100);
        }

        let adjustedBasePrice = round2((qty || 0) * unit);
        unit = round2(unit);

        let setup = 0;
        if (selectedProductType === 'SUPPLIED_PRODUCTS') {
          if (setupType === 'Repeat') {
            setup = decoration.repeatSetup ?? 0;
          } else {
            setup = decoration.newSetup ?? 0;
          }
        }

        let servicesTotal = 0;
        if (services.length && qty) {
          decoration.specialService.forEach((service) => {
            if (services.includes(service.name)) {
              servicesTotal += service.price * (qty || 0);
            }
          });
        }
        servicesTotal = round2(servicesTotal);

        const subtotal = round2(
          adjustedBasePrice + setup + servicesTotal + position.moqSurcharge,
        );

        return {
          unit,
          line: adjustedBasePrice,
          setup,
          services: servicesTotal,
          moqSurcharge: position.moqSurcharge,
          subtotal,
        };
      }

      if (type === 'Power-Print' || type === 'Power-Sticker') {
        let unit = findPowerPrintUnitPrice(qty || 0, size, decoration.prices);
        if (qty < moqQty && decoration.moqLogic === 'Logic 1') {
          const price1 = findPowerPrintUnitPrice(
            moqQty || 0,
            size,
            decoration.prices,
          );
          unit = (moqQty * price1) / qty;
        }

        if (hasSpecialMaterial && specialMaterialType.length > 0) {
          let specialMaterialPercentage = 0;
          specialMaterialType.forEach((smType) => {
            const sm = decoration?.specialMaterial.find(
              (sm) => sm.name === smType,
            );
            if (sm) {
              specialMaterialPercentage += sm.price;
            }
          });
          specialMaterialPercentage =
            specialMaterialPercentage > multiplierLimit
              ? multiplierLimit
              : specialMaterialPercentage;

          unit = unit * ((100 + specialMaterialPercentage) / 100);
        }

        let adjustedBasePrice = round2((qty || 0) * unit);
        unit = round2(unit);

        let setup = 0;
        if (selectedProductType === 'SUPPLIED_PRODUCTS') {
          if (setupType === 'Repeat') {
            setup = decoration.repeatSetup ?? 0;
          } else {
            setup = decoration.newSetup ?? 0;
          }
        }

        let servicesTotal = 0;
        if (services.length && qty) {
          decoration.specialService.forEach((service) => {
            if (services.includes(service.name)) {
              servicesTotal += service.price * (qty || 0);
            }
          });
        }
        servicesTotal = round2(servicesTotal);

        const subtotal = round2(
          adjustedBasePrice + setup + servicesTotal + position.moqSurcharge,
        );

        return {
          unit,
          line: adjustedBasePrice,
          setup,
          services: servicesTotal,
          moqSurcharge: position.moqSurcharge,
          subtotal,
        };
      }

      if (type === 'Screen-Print') {
        let unit = findScreenPrintUnitPrice(
          qty || 0,
          numColors || 1,
          decoration.prices,
        );
        if (qty < moqQty && decoration.moqLogic === 'Logic 1') {
          const price1 = findScreenPrintUnitPrice(
            moqQty || 0,
            numColors || 1,
            decoration.prices,
          );
          unit = (moqQty * price1) / qty;
        }

        if (hasSpecialMaterial && specialMaterialType.length > 0) {
          let specialMaterialPercentage = 0;
          specialMaterialType.forEach((smType) => {
            const sm = decoration?.specialMaterial.find(
              (sm) => sm.name === smType,
            );
            if (sm) {
              specialMaterialPercentage += sm.price;
            }
          });
          specialMaterialPercentage =
            specialMaterialPercentage > multiplierLimit
              ? multiplierLimit
              : specialMaterialPercentage;

          unit = unit * ((100 + specialMaterialPercentage) / 100);
        }

        let adjustedBasePrice = round2((qty || 0) * unit);
        unit = round2(unit);

        let setup = 0;
        if (selectedProductType === 'SUPPLIED_PRODUCTS') {
          if (setupType === 'Repeat') {
            setup = decoration.repeatSetup ?? 0;
          } else {
            setup = decoration.newSetup ?? 0;
          }
          setup = setup * (numColors || 1);
        }

        let servicesTotal = 0;
        if (services.length && qty) {
          decoration.specialService.forEach((service) => {
            if (services.includes(service.name)) {
              servicesTotal += service.price * (qty || 0);
            }
          });
        }
        servicesTotal = round2(servicesTotal);

        const subtotal = round2(
          adjustedBasePrice + setup + servicesTotal + position.moqSurcharge,
        );

        return {
          unit,
          line: adjustedBasePrice,
          setup,
          services: servicesTotal,
          moqSurcharge: position.moqSurcharge,
          subtotal,
        };
      }

      if (type === 'Gang-Sheet') {
        let unit = findGangSheetUnitPrice(qty || 0, decoration.prices);
        if (qty < moqQty && decoration.moqLogic === 'Logic 1') {
          const price1 = findGangSheetUnitPrice(moqQty || 0, decoration.prices);
          unit = (moqQty * price1) / qty;
        }

        if (hasSpecialMaterial && specialMaterialType.length > 0) {
          let specialMaterialPercentage = 0;
          specialMaterialType.forEach((smType) => {
            const sm = decoration?.specialMaterial.find(
              (sm) => sm.name === smType,
            );
            if (sm) {
              specialMaterialPercentage += sm.price;
            }
          });
          specialMaterialPercentage =
            specialMaterialPercentage > multiplierLimit
              ? multiplierLimit
              : specialMaterialPercentage;

          unit = unit * ((100 + specialMaterialPercentage) / 100);
        }

        let adjustedBasePrice = round2((qty || 0) * unit);
        unit = round2(unit);

        let setup = 0;
        if (selectedProductType === 'SUPPLIED_PRODUCTS') {
          if (setupType === 'Repeat') {
            setup = decoration.repeatSetup ?? 0;
          } else {
            setup = decoration.newSetup ?? 0;
          }
        }

        let servicesTotal = 0;
        if (services.length && qty) {
          decoration.specialService.forEach((service) => {
            if (services.includes(service.name)) {
              servicesTotal += service.price * (qty || 0);
            }
          });
        }
        servicesTotal = round2(servicesTotal);

        const subtotal = round2(
          adjustedBasePrice + setup + servicesTotal + position.moqSurcharge,
        );

        return {
          unit,
          line: adjustedBasePrice,
          setup,
          services: servicesTotal,
          moqSurcharge: position.moqSurcharge,
          subtotal,
        };
      }
    }

    function findScreenPrintUnitPrice(qty, numColors, pricingGrid) {
      const validColors = Math.max(1, Math.min(8, numColors || 1));

      for (const tier of pricingGrid) {
        const range = tier.quantity_range;
        let min, max;
        if (range.includes('+')) {
          min = Number(range.replace('+', ''));
          max = Infinity;
        } else {
          [min, max] = range.split('-').map(Number);
        }

        if (qty >= min && qty <= max) {
          const colorKey = `${validColors}_col`;
          if (tier.prices && tier.prices[colorKey] !== undefined) {
            return tier.prices[colorKey];
          }
        }
      }

      return 0;
    }

    function findPowerPrintUnitPrice(qty, size, pricingGrid) {
      for (const tier of pricingGrid) {
        const range = tier.quantity_range;
        let min, max;
        if (range.includes('+')) {
          min = Number(range.replace('+', ''));
          max = Infinity;
        } else {
          [min, max] = range.split('-').map(Number);
        }

        if (qty >= min && qty <= max) {
          if (tier.prices && tier.prices[size] !== undefined) {
            return tier.prices[size];
          }
        }
      }

      return 0;
    }

    function findEmbroideryUnitPrice(qty, stitches, pricingGrid) {
      for (const tier of pricingGrid) {
        const rangeStr = tier.quantity_range;
        let min, max;

        if (rangeStr.includes('+')) {
          min = parseInt(rangeStr.replace('+', ''));
          max = Infinity;
        } else {
          [min, max] = rangeStr.split('-').map(Number);
        }
        if (qty >= min && qty <= max) {
          const stitchKeys = Object.keys(tier.stitchs);

          const maxIncludedStitchK = Math.max(
            ...stitchKeys.map((key) => {
              const [, maxK] = key.replace('K', '').split('-').map(Number);
              return maxK;
            }),
          );

          const calculateExtraStitchCharge = (stitches) => {
            if (stitches <= maxIncludedStitchK) return 0;
            return Math.ceil(stitches - maxIncludedStitchK) * 0.2;
          };

          const stitchRanges = Object.entries(tier.stitchs).sort((a, b) => {
            const aMin = Number(a[0].replace('K', '').split('-')[0]);
            const bMin = Number(b[0].replace('K', '').split('-')[0]);
            return aMin - bMin;
          });
          for (let i = 0; i < stitchRanges.length; i++) {
            const [range, price] = stitchRanges[i];
            const [stitchMin, stitchMax] = range
              .replace('K', '')
              .split('-')
              .map(Number);

            const isLast = i === stitchRanges.length - 1;

            if (isLast) {
              if (stitches >= stitchMin) {
                return price + calculateExtraStitchCharge(stitches);
              }
            } else {
              if (stitches >= stitchMin && stitches < stitchMax) {
                return price + calculateExtraStitchCharge(stitches);
              }
            }
          }
        }
      }

      return 0;
    }

    function findGangSheetUnitPrice(qty, pricingGrid) {
      for (const tier of pricingGrid) {
        const range = tier.quantity_range;
        let min, max;
        if (range.includes('+')) {
          min = Number(range.replace('+', ''));
          max = Infinity;
        } else {
          [min, max] = range.split('-').map(Number);
        }

        if (qty >= min && qty <= max) {
          if (tier.prices && tier.prices.price !== undefined) {
            return tier.prices.price;
          }
        }
      }

      return 0;
    }

    function recalculateAndRender() {
      // Preserve focus and caret: if user is typing in an input, restore after re-render
      let focusTargetId = null;
      let focusTargetDataset = null;
      let caretPos = null;
      const active = document.activeElement;
      if (
        active &&
        active.tagName === 'INPUT' &&
        (active.dataset.positionId || active.dataset.blankId)
      ) {
        if (active.id) {
          focusTargetId = active.id;
        } else {
          focusTargetDataset = {
            positionId: active.dataset.positionId,
            role: active.dataset.role,
          };
        }
        caretPos = { start: active.selectionStart, end: active.selectionEnd };
      }

      const calculations = calculate();

      calculations.positions.forEach(({ id, breakdown }) => {
        const position = addedPositions.find((p) => p.id === id);
        if (position) {
          position.subtotal = breakdown?.subtotal || 0;
        }
      });

      render(calculations);

      bindEvents();

      // Restore focus and caret so cursor is not lost when typing in qty/colors/stitches
      let focusEl = null;
      if (focusTargetId) {
        focusEl = document.getElementById(focusTargetId);
      } else if (focusTargetDataset) {
        const { positionId, role } = focusTargetDataset;
        focusEl = document.querySelector(
          `[data-position-id="${positionId}"][data-role="${role}"]`,
        );
      }
      if (focusEl) {
        focusEl.focus();
        // Restore cursor position (text inputs support setSelectionRange; number inputs do not)
        if (
          caretPos != null &&
          typeof focusEl.setSelectionRange === 'function' &&
          !['radio', 'checkbox'].includes(focusEl.type)
        ) {
          const val = String(focusEl.value || '');
          const len = val.length;
          const start = Math.min(Math.max(0, caretPos.start), len);
          const end = Math.min(Math.max(0, caretPos.end), len);
          focusEl.setSelectionRange(start, end);
        }
      }
    }

    function render(calculations) {
      renderProductTypeSelection();
      if (selectedProductType) {
        if (singleProduct) {
          renderBlankProducts();
        }
        renderPositions(calculations);
        renderSummary(calculations);
      }
    }

    // PC2A only: render the standalone Blank Products section (renders once;
    // all interactions update state directly without re-rendering).
    function renderBlankProducts() {
      const root = document.getElementById('blank-products-root');
      if (!root) return;
      if (root.dataset.rendered === 'true') return;

      // Quantity stepper that mirrors the Position quantity input UI.
      const stepper = (entry) => `
        <div class="flex items-center overflow-hidden bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 focus-within:border-[#354e5c] focus-within:shadow-[0_0_0_3px_rgba(0,166,80,0.15)]">
          <button type="button"
            class="number-decrement w-11 h-11 flex items-center justify-center text-[20px] font-medium text-black bg-gray-50 transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
            data-role="blank-qty-decrement" data-blank-id="${entry.id}" aria-label="Decrease quantity">−</button>
          <input type="text" inputmode="numeric" pattern="[0-9]*"
            id="blank-qty-${entry.id}"
            class="w-full border-0 px-3 py-3 text-base font-medium focus:ring-0 focus:outline-none flex-1 text-center text-[16px] bg-transparent"
            min="0" value="${entry.qty}"
            data-role="blank-qty" data-blank-id="${entry.id}" aria-required="true">
          <button type="button"
            class="number-increment w-11 h-11 flex items-center justify-center text-[20px] font-medium text-black bg-gray-50 transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
            data-role="blank-qty-increment" data-blank-id="${entry.id}" aria-label="Increase quantity">+</button>
        </div>
      `;

      // No size pricing: single quantity row.
      if (!activeSizePricing) {
        const entry = blankProducts[0];
        if (!entry) return;
        const moq = getBlankMoq(entry.size);
        root.innerHTML = `
          <div class="space-y-1.5">
            <div class="w-1/2 sm:w-1/4 text-center text-sm font-semibold text-gray-700">Quantity <span class="text-gray-500 text-xs ml-1 font-normal">MOQ: ${moq}</span></div>
            <div class="w-1/2 sm:w-1/4">${stepper(entry)}</div>
          </div>
        `;
        root.dataset.rendered = 'true';
        return;
      }

      // Active size pricing: one fixed row per size (Size + Quantity, each 1/4 width).
      const moq = getBlankMoq();
      const rowsHtml = blankProducts
        .map(
          (entry) => `
            <div class="flex items-center gap-4">
              <div class="flex-1 sm:flex-none sm:w-1/4 text-center text-base font-semibold text-gray-900">${entry.size}</div>
              <div class="flex-1 sm:flex-none sm:w-1/4">${stepper(entry)}</div>
            </div>
          `,
        )
        .join('');

      root.innerHTML = `
        <div class="space-y-3">
          <div class="flex items-center gap-4">
            <div class="flex-1 sm:flex-none sm:w-1/4 text-center text-sm font-semibold text-gray-700">Size</div>
            <div class="flex-1 sm:flex-none sm:w-1/4 text-center text-sm font-semibold text-gray-700">Quantity <span class="text-gray-500 text-xs ml-1 font-normal">MOQ: ${moq}</span></div>
          </div>
          ${rowsHtml}
        </div>
      `;
      root.dataset.rendered = 'true';
    }

    function renderProductTypeSelection() {
      const selectionRoot = document.getElementById('product-type-selection');
      const positionsSection = document.getElementById('positions-section');
      const positionsTitle = document.getElementById('positions-title');
      if (!selectionRoot || !positionsSection) return;

      const hasPositions = addedPositions.length > 0;
      if (selectedProductType) {
        if (hasPositions) {
          selectionRoot.classList.add('hidden');
          positionsSection.classList.remove('hidden');
        } else {
          selectionRoot.classList.remove('hidden');
          positionsSection.classList.remove('hidden');
        }

        if (positionsTitle) {
          if (selectedProductType === 'SUPPLIED_PRODUCTS') {
            positionsTitle.textContent =
              'Supplied Products - Select Your Position';
          } else if (selectedProductType === 'CONSUMABLES') {
            positionsTitle.textContent = 'Consumables - Select Your Position';
          }
        }

        const selectedRadio = document.querySelector(
          `input[data-role="product-type-radio"][value="${selectedProductType}"]`,
        );
        if (selectedRadio) {
          selectedRadio.checked = true;
          const label = selectedRadio.closest('label');
          if (label) {
            label.classList.add('border-[#00a650]');
            label.classList.remove(
              'border-gray-200',
              'hover:border-gray-300',
              'cursor-pointer',
            );
          }
        }

        const radios = document.querySelectorAll(
          'input[data-role="product-type-radio"]',
        );
        radios.forEach((radio) => {
          if (selectedRadio && radio !== selectedRadio) {
            radio.disabled = false;
            const label = radio.closest('label');
            if (label) {
              label.classList.add(
                'border-gray-200',
                'hover:border-gray-300',
                'cursor-pointer',
              );
            }
          }
        });
      } else {
        selectionRoot.classList.remove('hidden');
        positionsSection.classList.add('hidden');

        const radios = document.querySelectorAll(
          'input[data-role="product-type-radio"]',
        );
        radios.forEach((radio) => {
          radio.checked = false;
          radio.disabled = false;
          const label = radio.closest('label');
          if (label) {
            label.classList.remove('border-[#00a650]');
            label.classList.add(
              'border-gray-200',
              'hover:border-gray-300',
              'cursor-pointer',
            );
          }
        });
      }
    }

    function renderPositions(calculations) {
      const root = document.getElementById('positions-root');
      if (!root) return;

      const infoRoot = document.getElementById('position-info');
      if (!infoRoot) return;

      const maxPositions =
        selectedProductType === 'SUPPLIED_PRODUCTS'
          ? PC2BData.maxPositionsUnbranded
          : PC2BData.maxPositionsConsumable;
      const currentCount = addedPositions.length;
      const remaining = maxPositions - currentCount;

      if (remaining > 0) {
        infoRoot.innerHTML = `<span class="text-gray-600">Max positions: ${maxPositions} • <span class="font-semibold text-gray-900">${remaining} remaining</span></span>`;
      } else {
        infoRoot.innerHTML = `<span class="text-gray-600">Max positions: ${maxPositions} • <span class="font-semibold text-gray-900">No positions remaining</span></span>`;
      }

      if (addedPositions.length === 0) {
        root.innerHTML = `
          <div class="text-center py-16 px-4">
            <div class="max-w-md mx-auto">
              <div class="mb-6">
                <svg class="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">Get Started</h3>
              <p class="text-gray-600 mb-6">Add your first position to begin configuring your decoration quote.</p>
              <button 
                id="add-position-empty"
                type="button"
                class="${
                  pc2b
                    ? 'bg-[#00a650] hover:bg-[#008f45]'
                    : 'bg-[#354e5c] hover:bg-[#2a3d47]'
                }  text-white rounded-xl px-6 py-3 text-base font-semibold transition-all flex items-center space-x-3 mx-auto duration-200 border-2 hover:-translate-y-[1px] border-none shadow-[0_2px_4px_rgba(0,166,80,.2),0_0_0_0.5px_rgba(0,166,80,.4)] hover:shadow-[0_4px_8px_rgba(0,166,80,.3),0_0_0_0.5px_rgba(0,166,80,.5)] min-w-[240px]">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                <span class="ml-2">Add First Position</span>
              </button>
            </div>
          </div>
        `;
        return;
      }

      const calculationsMap = {};
      if (calculations && calculations.positions) {
        calculations.positions.forEach((p) => {
          calculationsMap[p.id] = p.breakdown;
        });
      }

      const allCollapsed =
        addedPositions.length > 0 && addedPositions.every((p) => p._collapsed);

      let positionsHtml = addedPositions
        .map((position) => {
          const breakdown = calculationsMap[position.id] || { subtotal: 0 };
          return renderPositionCard(position, breakdown);
        })
        .join('');

      if (allCollapsed && remaining > 0) {
        positionsHtml += `
          <div class="mt-6 flex justify-center">
            <button 
              id="add-position-after"
              type="button"
              class="${
                pc2b
                  ? 'bg-[#00a650] hover:bg-[#008f45]'
                  : 'bg-[#354e5c] hover:bg-[#2a3d47]'
              } text-white rounded-xl px-6 py-3 text-base font-semibold transition-all shadow-md flex items-center space-x-2 duration-200 hover:-translate-y-[1px] hover:shadow-[0_4px_12px_rgba(0,166,80,.3)]">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              <span>Add Position</span>
            </button>
          </div>
        `;
      } else if (allCollapsed && remaining === 0) {
        positionsHtml += `
          <div class="mt-6 text-center">
            <p class="text-sm text-gray-500">Maximum positions (${maxPositions}) reached</p>
          </div>
        `;
      }

      root.innerHTML = positionsHtml;
    }

    function renderPositionCard(position, breakdown) {
      const { id, decorationId, type, hasSubtype } = position;
      const decorations =
        selectedProductType === 'SUPPLIED_PRODUCTS'
          ? PC2BData.unbrandedDecorations
          : PC2BData.consumableDecorations;
      const decoration = getDecorationDataById(decorationId);

      const subtotal = breakdown ? breakdown.subtotal || 0 : 0;
      const positionIndex = position.id.split('-')[1];
      const isCollapsed = position._collapsed || false;
      const contentId = `content-${id}`;

      let isComplete = false;
      if (type && position.qty && position.setupType) {
        if (type === 'Screen-Print') {
          isComplete = position.numColors;
        }
        if (type === 'Embroidery') {
          isComplete = position.stitches && position.stitches > 0;
        }
        if (type === 'Power-Print' || type === 'Power-Sticker') {
          isComplete = position.size !== null && position.size !== undefined;
        }
        if (type === 'Gang-Sheet') {
          isComplete = true;
        }
      }

      let selectedServices = [];
      if (position.material === 'SPECIAL' && position.services.length > 0) {
        selectedServices = position.services;
      }

      let html = `
        <div class="position-card rounded-2xl border-2 ${
          isCollapsed
            ? 'border-gray-300 bg-gray-50'
            : 'border-gray-200 bg-white'
        } shadow-sm mb-6 transition-all duration-200 hover:border-gray-300 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]" 
            data-position-id="${id}">
          <div class="flex items-center justify-between p-2 lg:p-6 ${
            isCollapsed
              ? 'cursor-pointer transition-colors'
              : 'border-b border-gray-200'
          }" 
              ${
                isCollapsed
                  ? `data-role="collapsed-card-header" data-position-id="${id}"`
                  : ''
              }>
            <div class="flex items-center space-x-3 flex-1 min-w-0">
              <h3 class="text-sm font-bold text-gray-900 whitespace-nowrap" data-role="position-title">Position #${positionIndex}</h3>
              ${
                isCollapsed
                  ? `
                <div class="flex items-center space-x-2 text-xs flex-1 min-w-0 overflow-hidden">
                  <span class="px-1.5 py-0.5 ${
                    pc2b ? 'bg-[#00a650]' : 'bg-[#354e5c]'
                  } text-white rounded-full font-semibold text-xs whitespace-nowrap flex-shrink-0">✓</span>
                  <span class="text-gray-700 font-medium whitespace-nowrap flex-shrink-0">${
                    decoration.name
                  }</span>
                  <span class="text-gray-400 flex-shrink-0">•</span>
                  <span class="text-gray-700 whitespace-nowrap flex-shrink-0">Qty: <span class="font-semibold">${
                    position.qty || 0
                  }</span></span>
                  <span class="text-gray-400 flex-shrink-0">•</span>
                  <span class="text-gray-700 whitespace-nowrap flex-shrink-0">Color: <span class="font-semibold">${
                    position.numColors || 1
                  }</span></span>
                  ${
                    selectedProductType === 'SUPPLIED_PRODUCTS'
                      ? `<span class="text-gray-400 flex-shrink-0">•</span>
                          <span class="text-gray-700 whitespace-nowrap flex-shrink-0"><span class="font-semibold">${
                            position.setupType ? position.setupType : '-'
                          }</span></span>`
                      : ''
                  }                  
                  ${
                    selectedServices.length > 0
                      ? `<span class="text-gray-400 flex-shrink-0">•</span><span class="text-gray-700 whitespace-nowrap flex-shrink-0"><span class="font-semibold">${
                          selectedServices.length > 1
                            ? selectedServices[0] +
                              ' +' +
                              (selectedServices.length - 1)
                            : selectedServices[0]
                        }</span></span>`
                      : ''
                  }
                </div>
                `
                  : ''
              }
        </div>
        <div class="flex items-center space-x-2">
          ${
            isCollapsed
              ? `
              <button type="button" 
              class="text-[#ed1c24] hover:text-[#c4161d] p-1.5 rounded transition-colors"
              data-position-id="${id}"
              data-role="expand-position"
              aria-label="Expand position">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            `
              : ''
          }
          <button type="button" 
            class="text-[#ed1c24] hover:text-[#c4161d] p-1.5 rounded transition-colors"
            data-position-id="${id}"
            data-role="remove-position"
            aria-label="Remove position">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>

      <div id="${contentId}" class="position-content overflow-hidden transition-all duration-300 ease-in-out ${
        isCollapsed ? 'hidden max-h-0 m-0 p-0 opacity-0' : ''
      }" data-role="position-content">
        <div class="p-6 md:p-8 space-y-8">      
          ${renderDecorationType(position, decorations, false)}
      
          ${hasSubtype ? renderDecorationType(position, decorations, true) : ''}

          ${type ? renderOptions(position, decoration) : ''}
          
          ${
            type && position.material === 'SPECIAL'
              ? renderServices(position, decoration)
              : ''
          }
          
          ${
            type && breakdown
              ? `
            <div class="pt-6 border-t-2 border-gray-200">
              <div class="flex justify-between items-center mb-2">
                <span class="text-base font-semibold text-gray-900">Position Subtotal:</span>
                <span class="text-xl font-bold text-gray-900" data-role="position-subtotal">${formatCurrency(
                  subtotal,
                )}</span>
              </div>
              <div class="flex justify-between items-center gap-2 mt-4">
                <div class="text-base text-red-600">
                  ${
                    position.pricingFile
                      ? `
                          For more details, please refer to the <a href="${position.pricingFile}" class="underline" target="_blank">decoration pricing</a>.
                          `
                      : ''
                  }
                </div>
                ${
                  isComplete
                    ? `
                    <button type="button" 
                      class="${
                        pc2b
                          ? 'bg-[#00a650] hover:bg-[#008f45]'
                          : 'bg-[#354e5c] hover:bg-[#2a3d47]'
                      } text-white text-sm font-medium px-6 py-2 rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_4px_8px_rgba(0,0,0,0.3)]"
                      data-position-id="${id}"
                      data-role="complete-position"
                      aria-label="Confirm position">
                      ✓ Confirm
                    </button>
                  `
                    : ''
                }
              </div>
            </div>
          `
              : ''
          }
        </div>
      </div>
    </div>
  `;

      return html;
    }

    function renderDecorationType(position, decorations, isSubtype) {
      const { id, subType } = position;

      let html = '<fieldset class="space-y-3" data-role="decoration-type">';
      html += `<legend class="block text-base font-semibold text-gray-900">${
        isSubtype ? 'Subtype' : 'Decoration Name'
      } <span class="text-red-500">*</span></legend>`;

      html +=
        '<div class="grid grid-cols-1 lg:grid-cols-2 gap-4" role="radiogroup" aria-label="Decoration type selection">';

      let data = decorations;
      if (isSubtype && selectedDecorationIndex != null) {
        data = decorations[selectedDecorationIndex].decorations;
      }

      data.forEach((decoration, index) => {
        let isSelected = false;
        if (isSubtype) {
          if (subType && decoration.name == subType) {
            isSelected = true;
          }
        } else {
          if (index == selectedDecorationIndex) {
            isSelected = true;
          }
        }
        const uniqueId = isSubtype
          ? decoration.name
          : decoration.decorationName;
        html += `
      <label class="decoration-${
        isSubtype ? 'subtype' : 'type'
      }-option flex items-center justify-center min-h-20 duration-200 rounded-2xl border-2 p-2 lg:p-6 cursor-pointer transition-all ${
        isSelected
          ? `${
              pc2b ? 'border-[#00a650]' : 'border-[#354e5c]'
            } border-[3px] bg-[rgba(0,166,80,0.08)] shadow-[0_0_0_1px_rgba(0,166,80,0.1)]`
          : 'border-gray-200 bg-white'
      }" for="${uniqueId}" tabindex="0">
        <input type="radio" 
          id="${uniqueId}"
          name="decoration-${isSubtype ? 'subtype' : 'type'}-${id}" 
          value="${uniqueId}"
          class="peer sr-only"
          ${isSelected ? 'checked' : ''}
          data-type="${uniqueId}"
          data-position-id="${id}">
        <div class="font-semibold text-base text-center peer-checked:font-semibold">${uniqueId}</div>
      </label>
    `;
      });

      html += '</div>';
      if (!isSubtype) {
        html +=
          '<p class="text-xs text-gray-500 mt-3">Each position allows only one decoration type.</p>';
      }
      html += '</fieldset>';
      return html;
    }

    function renderOptions(position, decoration) {
      const {
        id,
        type,
        qty,
        numColors,
        size,
        stitches,
        setupType,
        hasSpecialMaterial,
        specialMaterialType,
        material,
        services,
      } = position;

      // Check if this is Power Print within Consumables
      const isConsumablesPowerPrint =
        selectedProductType === 'CONSUMABLES' &&
        (type === 'Power-Print' || type === 'Power-Sticker');
      const defaultQty = decoration.defaultQty;
      const colorLimit = [1, decoration.maxColour];
      const showColors = type === 'Screen-Print';

      let html = '<fieldset class="space-y-4" data-role="options">';
      html += '<legend class="sr-only">Position Options</legend>';

      // Quantity
      const qtyInputId = `qty-${id}`;
      const displayQty = qty || defaultQty;
      html += `
        <div class="qty-field">
          <label for="${qtyInputId}" class="block text-sm text-gray-700 mb-2 font-semibold">
            Quantity
            ${
              type
                ? `<span class="text-gray-500 text-xs ml-1 font-normal">MOQ: ${defaultQty}</span>`
                : ''
            }
          </label>
          <div class="flex items-center overflow-hidden bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 ${
            pc2b
              ? 'focus-within:border-[#00a650]'
              : 'focus-within:border-[#354e5c]'
          } focus-within:shadow-[0_0_0_3px_rgba(0,166,80,0.15)]" data-position-id="${id}">
            <button type="button" 
              class="number-decrement w-11 h-11 flex items-center justify-center text-[20px] font-medium text-black bg-gray-50 transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
              data-position-id="${id}"
              data-role="qty-decrement"
              aria-label="Decrease quantity">−</button>
            <input type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              id="${qtyInputId}"
              class="w-full rounded-lg border-0 px-3 py-3 text-base font-medium focus:ring-0 focus:outline-none ${
                pc2b ? 'focus:border-[#00a650]' : 'focus:border-[#354e5c]'
              } focus:shadow-[0_0_0_3px_rgba(0,166,80,0.15)] flex-1 text-center text-[16px] bg-transparent border-none" 
              min="1"
              value="${displayQty}" 
              data-position-id="${id}"
              data-role="qty-input"
              aria-required="true">
            <button type="button" 
              class="number-increment w-11 h-11 flex items-center justify-center text-[20px] font-medium text-black bg-gray-50 transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
              data-position-id="${id}"
              data-role="qty-increment"
              aria-label="Increase quantity">+</button>
          </div>
          ${
            type
              ? `<p class="text-xs text-gray-500 mt-2">${decoration.quantityDisclaimer}</p>`
              : ''
          }
        </div>
      `;

      // Colors
      if (showColors) {
        const colorsInputId = `colors-${id}`;
        html += `
          <div class="colors-field">
            <label for="${colorsInputId}" class="block text-base font-semibold text-gray-900 mb-3">
              Colours
            </label>
            <div class="flex items-center overflow-hidden bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 ${
              pc2b
                ? 'focus-within:border-[#00a650]'
                : 'focus-within:border-[#354e5c]'
            } focus-within:shadow-[0_0_0_3px_rgba(0,166,80,0.15)] max-w-xs" data-position-id="${id}">
              <button type="button" 
                class="number-decrement w-11 h-11 flex items-center justify-center text-[20px] font-medium text-black bg-gray-50 transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                data-position-id="${id}"
                data-role="colors-decrement"
                aria-label="Decrease colors">−</button>
              <input type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                id="${colorsInputId}"
                class="w-full rounded-lg border-0 px-3 py-3 text-base font-medium focus:ring-0 focus:outline-none text-center  ${
                  pc2b
                    ? 'focus-within:border-[#00a650]'
                    : 'focus-within:border-[#354e5c]'
                } focus:shadow-[0_0_0_3px_rgba(0,166,80,0.15)] flex-1 text-[16px] bg-transparent border-none" 
                min="${colorLimit[0]}" 
                max="${colorLimit[1]}" 
                value="${numColors || 1}" 
                data-position-id="${id}"
                data-role="colors-input"
                aria-required="true">
              <button type="button" 
                class="number-increment w-11 h-11 flex items-center justify-center text-[20px] font-medium text-black bg-gray-50 transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                data-position-id="${id}"
                data-role="colors-increment"
                aria-label="Increase colors">+</button>
            </div>
            <p class="text-xs text-gray-500 mt-2">${
              decoration.colourDisclaimer
            }</p>
          </div>
        `;
      }

      // Stitches input (only for Embroidery)
      if (type === 'Embroidery') {
        const stitchesInputId = `stitches-${id}`;
        html += `
          <div class="stitches-field">
            <label for="${stitchesInputId}" class="block text-base font-semibold text-gray-900 mb-3">
              Stitches
            </label>
            <div class="flex items-center gap-2 max-w-xs">
              <input type="text"
                inputmode="decimal"
                pattern="[0-9]*"
                id="${stitchesInputId}"
                class="w-full rounded-lg border-2 border-gray-200 px-4 py-3 text-base text-center font-medium focus:ring-0 focus:outline-none ${
                  pc2b ? 'focus:border-[#00a650]' : 'focus:border-[#354e5c]'
                } focus:shadow-[0_0_0_3px_rgba(0,166,80,0.15)]" 
                min="0.1" 
                value="${stitches || 1}" 
                data-position-id="${id}"
                data-role="stitches-input"
                aria-required="true">
                <span>K</span>
            </div>
            <p class="text-xs text-gray-500 mt-2">${
              decoration.stitchesDisclaimer
            }</p>
          </div>
        `;
      }

      if (type === 'Power-Print' || type === 'Power-Sticker') {
        html += `
          <div class="size-field">
            <label class="block text-base font-semibold text-gray-900 mb-3">
              Size
            </label>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-3" role="radiogroup" aria-label="Size selection">
              ${Object.keys(decoration.prices[0].prices)
                .map((sizeOpt) => {
                  const sizeInputId = `size-${id}-${sizeOpt}`;
                  return `
                <label class="size-option flex items-center justify-center min-h-20 duration-200 rounded-2xl border-2 p-2 lg:p-5 text-center cursor-pointer transition-all ${
                  size === sizeOpt
                    ? `border-[3px] ${
                        pc2b ? 'border-[#00a650]' : 'border-[#354e5c]'
                      } bg-[rgba(0,166,80,0.08)] shadow-[0_0_0_1px_rgba(0,166,80,0.1)]`
                    : 'border-gray-200 bg-white'
                }" for="${sizeInputId}">
                  <input type="radio" 
                    id="${sizeInputId}"
                    name="decoration-size-${id}" 
                    value="${sizeOpt}"
                    class="peer sr-only"
                    ${size === sizeOpt ? 'checked' : ''}
                    data-size="${sizeOpt}"
                    data-position-id="${id}"
                    aria-required="true">
                  <div class="font-semibold text-base peer-checked:font-semibold">${sizeOpt}</div>
                </label>
              `;
                })
                .join('')}
            </div>
            <p class="text-xs text-gray-500 mt-2">${
              decoration.sizeDisclaimer
            }</p>
          </div>
        `;
      }

      // Setup
      if (selectedProductType !== 'CONSUMABLES') {
        html += `
          <div class="setup-field">
            <label class="block text-base font-semibold text-gray-900 mb-3">
              Setup Type
            </label>
            <div class="grid grid-cols-2 gap-4" role="radiogroup" aria-label="Setup type selection">
              ${['New', 'Repeat']
                .map((st) => {
                  const setupInputId = `setup-${id}-${st}`;
                  return `
                <label class="setup-option flex items-center justify-center min-h-20 duration-200 rounded-2xl border-2 p-2 lg:p-6 cursor-pointer transition-all ${
                  setupType === st
                    ? `border-[3px] ${
                        pc2b ? 'border-[#00a650]' : 'border-[#354e5c]'
                      } bg-[rgba(0,166,80,0.08)] shadow-[0_0_0_1px_rgba(0,166,80,0.1)]`
                    : 'border-gray-200 bg-white'
                }" for="${setupInputId}">
                  <input type="radio" 
                    id="${setupInputId}"
                    name="setup-${id}" 
                    value="${st}"
                    class="peer sr-only"
                    ${setupType === st ? 'checked' : ''}
                    data-setup="${st}"
                    data-position-id="${id}"
                    aria-required="true">
                  <div class="font-semibold text-base text-center peer-checked:font-semibold">${st}</div>
                </label>
              `;
                })
                .join('')}
            </div>
            <p class="text-xs text-gray-500 mt-2">${
              decoration.setupDisclaimer
            }</p>
          </div>
        `;
      }

      // Special Material section (hidden for Power Print in Consumables)
      if (
        !isConsumablesPowerPrint &&
        decoration.hasSpecialMaterial &&
        decoration.specialMaterial.length > 0
      ) {
        html += `
          <div class="special-material-field">
            <label class="block text-base font-semibold text-gray-900 mb-3">
              Has Special Material
            </label>
            <div class="grid grid-cols-2 gap-4 mb-4" role="radiogroup" aria-label="Has Special Material selection">
              ${[
                { value: 'false', label: 'No' },
                { value: 'true', label: 'Yes' },
              ]
                .map(({ value: hasMat, label }) => {
                  const hasMatInputId = `has-special-material-${id}-${hasMat}`;
                  const isChecked =
                    (hasMat === 'true' && hasSpecialMaterial === true) ||
                    (hasMat === 'false' && hasSpecialMaterial === false);
                  return `
                <label class="has-special-material-option flex items-center justify-center min-h-20 duration-200 rounded-2xl border-2 p-2 lg:p-6 cursor-pointer transition-all ${
                  isChecked
                    ? `border-[3px] ${
                        pc2b ? 'border-[#00a650]' : 'border-[#354e5c]'
                      } bg-[rgba(0,166,80,0.08)] shadow-[0_0_0_1px_rgba(0,166,80,0.1)]`
                    : 'border-gray-200 bg-white'
                }" for="${hasMatInputId}">
                  <input type="radio" 
                    id="${hasMatInputId}"
                    name="has-special-material-${id}" 
                    value="${hasMat}"
                    class="sr-only"
                    ${isChecked ? 'checked' : ''}
                    data-has-special-material="${hasMat}"
                    data-position-id="${id}">
                  <div class="font-semibold text-base text-center">${label}</div>
                </label>
              `;
                })
                .join('')}
            </div>
            ${
              hasSpecialMaterial
                ? `
              <div class="special-material-type-field mt-4">
                <label class="block text-base font-semibold text-gray-900 mb-3">
                  Special Material
                </label>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-3" role="radiogroup" aria-label="Special Material selection">
                  ${decoration.specialMaterial
                    .map((mat) => {
                      const isChecked = specialMaterialType.includes(mat.name);
                      const matInputId = `special-material-type-${id}-${mat.name}`;
                      return `
                    <label class="special-material-type-option flex items-center justify-center min-h-20 duration-200 rounded-2xl border-2 p-2 lg:p-5 text-center cursor-pointer transition-all ${
                      isChecked
                        ? `border-[3px] ${
                            pc2b ? 'border-[#00a650]' : 'border-[#354e5c]'
                          } bg-[rgba(0,166,80,0.08)] shadow-[0_0_0_1px_rgba(0,166,80,0.1)]`
                        : 'border-gray-200 bg-white'
                    }" for="${matInputId}">
                      <input type="checkbox" 
                        id="${matInputId}"
                        value="${mat.name}"
                        class="sr-only"
                        ${isChecked ? 'checked' : ''}
                        data-special-material-type="${mat.name}"
                        data-position-id="${id}">
                      <div class="font-semibold text-base">${mat.name}</div>
                    </label>
                  `;
                    })
                    .join('')}
                </div>
              </div>
            `
                : ''
            }
            <p class="text-xs text-gray-500 mt-2">${
              decoration.specialMaterialDisclaimer
            }</p>
          </div>
        `;
      }

      // Need Special Services section (hidden for Power Print in Consumables)
      if (
        !isConsumablesPowerPrint &&
        decoration.hasSpecialService &&
        decoration.specialService.length > 0
      ) {
        html += `
          <div class="material-field">
            <label class="block text-base font-semibold text-gray-900 mb-3">
              Need Special Services
            </label>
            <div class="grid grid-cols-2 gap-4" role="radiogroup" aria-label="Need Special Services selection">
              ${[
                { value: 'STANDARD', label: 'No' },
                { value: 'SPECIAL', label: 'Yes' },
              ]
                .map(({ value: mat, label }) => {
                  const materialInputId = `material-${id}-${mat}`;
                  return `
                <label class="material-option flex items-center justify-center min-h-20 duration-200 rounded-2xl border-2 p-2 lg:p-6 cursor-pointer transition-all ${
                  material === mat
                    ? `border-[3px] ${
                        pc2b ? 'border-[#00a650]' : 'border-[#354e5c]'
                      } bg-[rgba(0,166,80,0.08)] shadow-[0_0_0_1px_rgba(0,166,80,0.1)]`
                    : 'border-gray-200 bg-white'
                }" for="${materialInputId}">
                  <input type="radio" 
                    id="${materialInputId}"
                    name="material-${id}" 
                    value="${mat}"
                    class="peer sr-only"
                    ${material === mat ? 'checked' : ''}
                    data-material="${mat}"
                    data-position-id="${id}">
                  <div class="font-semibold text-base text-center peer-checked:font-semibold">${label}</div>
                </label>
              `;
                })
                .join('')}
            </div>
          </div>
          ${
            services.length == 0
              ? `<p class="text-xs text-gray-500 mt-2">${decoration.specialServicesDisclaimer}</p>`
              : ''
          }
        `;
      }

      html += '</fieldset>';
      return html;
    }

    function renderServices(position, decoration) {
      const { id, services } = position;

      let html = '<fieldset class="space-y-3" data-role="services">';
      html +=
        '<legend class="block text-base font-semibold text-gray-900 mb-4">';
      html += 'Special Services';
      html += '</legend>';

      html += '<div class="grid grid-cols-1 gap-3">';
      decoration.specialService.forEach((service) => {
        const isChecked = services.includes(service.name);
        const serviceInputId = `service-${id}-${service.name}`;
        html += `
          <label class="service-option min-h-20 duration-200 flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer transition-all ${
            isChecked
              ? `border-[3px] ${
                  pc2b ? 'border-[#00a650]' : 'border-[#354e5c]'
                } bg-[rgba(0,166,80,0.08)] shadow-[0_0_0_1px_rgba(0,166,80,0.1)]`
              : 'border-gray-200 bg-white'
          }" for="${serviceInputId}">
            <div class="flex items-center space-x-3">
              <input type="checkbox" 
                id="${serviceInputId}"
                class="sr-only" 
                ${isChecked ? 'checked' : ''}
                data-service="${service.name}"
                data-position-id="${id}"
                data-role="service-checkbox">
              <div class="flex-1">
                <div class="font-semibold text-base text-gray-900">${
                  service.name
                }</div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-base font-semibold text-gray-900">${formatCurrency(
                service.price,
              )}</div>
              <div class="text-xs text-gray-500">per item</div>
            </div>
          </label>
        `;
      });
      html += '</div>';

      html += `<p class="text-xs text-gray-500 mt-3">${decoration.specialServicesDisclaimer}</p>`;
      html += '</fieldset>';
      return html;
    }

    function renderSummary(calculations) {
      const root = document.getElementById('quote-summary');
      if (!root) return;

      const summary = calculations?.summary || {
        positionsTotal: 0,
        setupTotal: 0,
        servicesTotal: 0,
        gst: 0,
        grandTotal: 0,
      };

      const calculationsMap = {};
      if (calculations && calculations.positions) {
        calculations.positions.forEach((p) => {
          calculationsMap[p.id] = p.breakdown;
        });
      }

      let positionsHtml = '';
      if (addedPositions.length > 0) {
        positionsHtml = '<div class="space-y-1.5 mb-2 pb-2">';
        addedPositions.forEach((position) => {
          const breakdown = calculationsMap[position.id];
          if (breakdown) {
            const positionIndex = position.id.split('-')[1];

            let positionDetails = [];
            positionDetails.push(`Qty: ${position.qty || 0}`);

            if (position.type === 'Screen-Print') {
              positionDetails.push(`Colours: ${position.numColors || 1}`);
            } else if (position.type === 'Embroidery') {
              // Embroidery: Show Stitches
              positionDetails.push(`Stitches: ${position.stitches || 1}K`);
            } else {
              // Power Print: Show Size
              if (position.size) {
                positionDetails.push(`Size: ${position.size}`);
              }
            }

            if (selectedProductType === 'SUPPLIED_PRODUCTS') {
              positionDetails.push(`Setup: ${position.setupType}`);
            }

            // Show Special Material if selected
            if (position.specialMaterialType.length > 0) {
              positionDetails.push(
                `Special Material: ${position.specialMaterialType.join(', ')}`,
              );
            }
            if (position.services.length > 0) {
              positionDetails.push(position.services.join(', '));
            }

            positionsHtml += `
              <div class="text-xs">
                <div class="flex justify-between items-start mb-0.5">
                  <span class="font-bold text-gray-700">Position #${positionIndex}</span>
                  <span class="text-gray-900 font-bold">${formatCurrency(
                    breakdown.subtotal,
                  )}</span>
                </div>
                <div class="text-gray-600 pl-2 space-y-0.5">
                  <div>${position.name}</div>
                  ${positionDetails
                    .map((detail) => `<div>${detail}</div>`)
                    .join('')}
                </div>
              </div>
            `;
          }
        });
        positionsHtml += '</div>';
      }

      root.innerHTML = `
        <div class="space-y-2">
          <h2 class="text-base font-semibold text-gray-900 mb-2">Quote Summary</h2>
          
          ${positionsHtml}
          
          <div class="space-y-1.5">
            <div class="pt-1.5 border-t border-gray-200">
              <div class="flex justify-between text-xs mb-1" data-role="summary-gst">
                <span class="text-gray-600">GST (10%)</span>
                <span class="font-medium text-gray-900">${formatCurrency(
                  summary.gst,
                )}</span>
              </div>
              
              <div class="flex justify-between items-center pt-1 border-t border-gray-200" data-role="summary-total">
                <span class="text-sm font-semibold text-gray-900">Grand Total</span>
                <span class="text-lg font-bold text-gray-900">${formatCurrency(
                  summary.grandTotal,
                )}</span>
              </div>
            </div>
          </div>
          
          <div class="pt-2">
            <button type="button" 
              id="btn-view-quote"
              class="w-full bg-[#00a650] text-white hover:bg-[#008f45] rounded-xl px-4 py-2 text-xs font-medium transition-colors"
              data-role="view-quote">
              View Quote
            </button>
          </div>
          
          <p class="text-xs text-gray-500 mt-1.5">
            <strong>Note:</strong> GST (10%) applies to totals.
          </p>
        </div>
      `;
    }

    function renderQuoteModal(calculations) {
      const summary = calculations?.summary || {
        positionsTotal: 0,
        setupTotal: 0,
        servicesTotal: 0,
        gst: 0,
        grandTotal: 0,
      };

      const calculationsMap = {};
      if (calculations && calculations.positions) {
        calculations.positions.forEach((p) => {
          calculationsMap[p.id] = p.breakdown;
        });
      }

      const modalId = 'quote-modal';

      let positionsHtml = '';
      if (addedPositions.length > 0) {
        positionsHtml = '<div class="flex flex-wrap mb-6 gap-4">';
        addedPositions.forEach((position) => {
          const breakdown = calculationsMap[position.id];
          if (breakdown) {
            const positionIndex = position.id.split('-')[1];

            let positionDetails = [];

            if (position.type === 'Screen-Print') {
              // Screen Print: Show Colours
              positionDetails.push(`Colours: ${position.numColors || 1}`);
            } else if (position.type === 'Embroidery') {
              // Embroidery: Show Stitches
              positionDetails.push(`Stitches: ${position.stitches || 1}K`);
            } else {
              // Power Print: Show Size
              if (position.size) {
                positionDetails.push(`Size: ${position.size}`);
              }
            }

            if (selectedProductType === 'SUPPLIED_PRODUCTS') {
              positionDetails.push(`Setup: ${position.setupType}`);
            }

            // Show Special Material if selected
            if (position.specialMaterialType.length > 0) {
              positionDetails.push(
                `Special Material: ${position.specialMaterialType.join(', ')}`,
              );
            }

            if (position.services.length > 0) {
              positionDetails.push(position.services.join(', '));
            }

            positionsHtml += `
              <div class="flex flex-col rounded-xl border border-gray-200 bg-white p-3 shadow-sm basis-[calc(33.333%-1rem)]">
                <div class="flex-1">  
                  <h3 class="text-sm font-bold text-gray-900 mb-2">Position #${positionIndex}</h3>
                  <div class="space-y-0.5 text-xs text-gray-600 mb-2">
                    <div>Decoration Type: ${position.name}</div>
                    ${positionDetails
                      .map((detail) => `<div>${detail}</div>`)
                      .join('')}
                  </div>
                </div>
                <div class="mt-2 pt-2 border-t border-gray-200 space-y-0.5 text-xs text-gray-600">
                  <div class="flex justify-between">
                    <span>Decoration Unit Price:</span>
                    <span>${formatCurrency(breakdown.unit)}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Quantity:</span>
                    <span>${position.qty || 0}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Line Total:</span>
                    <span>${formatCurrency(breakdown.line)}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>MOQ Surcharge:</span>
                    <span>${formatCurrency(breakdown.moqSurcharge)}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Setup Fee:</span>
                    <span>${formatCurrency(breakdown.setup)}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Services Fee:</span>
                    <span>${formatCurrency(breakdown.services)}</span>
                  </div>                
                </div>
                <div class="flex justify-between mt-2 pt-2 border-t border-gray-200 font-medium text-xs text-gray-900">
                  <span>Subtotal:</span>
                  <span>${formatCurrency(breakdown.subtotal)}</span>
                </div>
              </div>
            `;
          }
        });
        positionsHtml += '</div>';
      } else {
        positionsHtml =
          '<p class="text-gray-500 text-center py-8">No positions added.</p>';
      }

      let html = `
        <div id="${modalId}" class="fixed inset-0 bg-black bg-opacity-50 z-50 hidden flex items-start justify-center pt-32 p-4" data-role="quote-modal">
          <div class="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[70vh] overflow-y-auto" onclick="event.stopPropagation();">
            <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between rounded-t-2xl">
              <h2 class="text-xl font-bold text-gray-900">Quote Details</h2>
              <button type="button" 
                class="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                data-role="close-quote-modal"
                data-modal-id="${modalId}"
                aria-label="Close modal">
                ×
              </button>
            </div>
            
            <div class="p-4 space-y-3" id="quote">
              ${positionsHtml}
              
              <div class="border-t-2 border-gray-200 pt-3 space-y-2">
                ${
                  singleProduct && calculations?.blanks
                    ? calculations.blanks
                        .map(
                          (blank) =>
                            `<div class="flex justify-between text-sm" data-role="summary-unbranded">
                        <span class="text-gray-600">Unbranded, Quantity: ${
                          blank.qty
                        }${blank.size ? `, Size: ${blank.size}` : ''}</span>
                        <span class="font-medium text-gray-900">${formatCurrency(
                          blank.total,
                        )} (${formatCurrency(blank.unit)} per unit)</span>
                      </div>`,
                        )
                        .join('')
                    : ''
                }
                <div class="flex justify-between text-sm" data-role="summary-positions">
                  <span class="text-gray-600">Positions Sub Total</span>
                  <span class="font-medium text-gray-900">${formatCurrency(
                    summary.positionsTotal,
                  )}</span>
                </div>
                
                <div class="pt-2 border-t border-gray-200">
                  <div class="flex justify-between text-sm mb-1.5" data-role="summary-gst">
                    <span class="text-gray-600">GST (10%)</span>
                    <span class="font-medium text-gray-900">${formatCurrency(
                      summary.gst,
                    )}</span>
                  </div>
                  
                  <div class="flex justify-between items-center pt-1.5 border-t border-gray-200" data-role="summary-total">
                    <span class="text-base font-semibold text-gray-900">Grand Total</span>
                    <span class="text-lg font-bold text-gray-900">${formatCurrency(
                      summary.grandTotal,
                    )}</span>
                  </div>
                </div>
              </div>
              
              <p class="text-xs text-gray-500 mt-2">
                <strong>Note:</strong> ${singleProduct ? 'This is applicable to JS products only. GST (10%) applies to totals.' : 'This is applicable to non-JS products and new decoration types only. GST (10%) applies to totals.'}
              </p>
            </div>
            
            <div class="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-3 flex justify-end space-x-3 rounded-b-2xl">
              <button type="button" 
                class="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                data-role="download-pdf"
                data-modal-id="${modalId}"
                onclick="handleDownloadPDF()">
                Download PDF
              </button>
              <button type="button" 
                class="px-6 py-2 ${
                  pc2b
                    ? 'bg-[#00a650] hover:bg-[#008f45]'
                    : 'bg-[#354e5c] hover:bg-[#2a3d47]'
                } text-white rounded-xl transition-colors font-medium"
                data-role="close-quote-modal"
                data-modal-id="${modalId}">
                Close
              </button>
            </div>
          </div>
        </div>
      `;

      return html;
    }

    function bindEvents() {
      bindProductTypeEvents();

      if (eventsBound) {
        return;
      }

      eventsBound = true;

      // PC2A only: Blank Products section events (delegated on the stable root).
      const blankRoot = document.getElementById('blank-products-root');
      if (blankRoot) {
        // Typing: update model only (no re-render, so the caret is preserved).
        blankRoot.addEventListener('input', (e) => {
          if (e.target.dataset.role !== 'blank-qty') return;
          handleBlankQtyChange(e.target.dataset.blankId, e.target.value);
        });
        // Stepper +/-: update model and write the value back into the input.
        blankRoot.addEventListener('click', (e) => {
          const role = e.target.dataset.role;
          if (
            role !== 'blank-qty-increment' &&
            role !== 'blank-qty-decrement'
          ) {
            return;
          }
          const blankId = e.target.dataset.blankId;
          const input = blankRoot.querySelector(
            `[data-role="blank-qty"][data-blank-id="${blankId}"]`,
          );
          if (!input) return;
          const currentValue = parseInt(input.value, 10) || 0;
          const next =
            role === 'blank-qty-increment'
              ? currentValue + 1
              : Math.max(0, currentValue - 1);
          input.value = next;
          handleBlankQtyChange(blankId, next);
        });
      }

      const positionsRoot = document.getElementById('positions-root');
      if (positionsRoot) {
        positionsRoot.addEventListener('click', (e) => {
          // Add First Position
          if (e.target.closest('#add-position-empty')) {
            e.stopPropagation();
            e.preventDefault();
            handleAddPosition();
            return;
          }

          // Remove Position
          const removeBtn = e.target.closest('[data-role="remove-position"]');
          if (removeBtn) {
            e.stopPropagation();
            e.preventDefault();
            handleRemovePosition(removeBtn.dataset.positionId);
            return;
          }

          // Add Position After First Position
          if (e.target.closest('#add-position-after')) {
            e.stopPropagation();
            e.preventDefault();
            handleAddPosition();
            return;
          }

          // Click Confirm Button
          const completeBtn = e.target.closest(
            '[data-role="complete-position"]',
          );
          if (completeBtn) {
            e.stopPropagation();
            e.preventDefault();
            handleCompletePosition(completeBtn.dataset.positionId);
            return;
          }

          // Expand Position
          const expandBtn = e.target.closest('[data-role="expand-position"]');
          if (expandBtn) {
            e.stopPropagation();
            e.preventDefault();
            handleExpandPosition(expandBtn.dataset.positionId);
            return;
          }
        });

        // Decoration Type Change
        positionsRoot.addEventListener('change', (e) => {
          if (
            e.target.type === 'radio' &&
            e.target.name &&
            e.target.name.startsWith('decoration-type-')
          ) {
            e.preventDefault();
            e.stopPropagation();
            handleDecorationTypeChange(
              e.target.dataset.positionId,
              e.target.value,
            );
          }
        });

        // Decoration Type Click
        positionsRoot.addEventListener('click', (e) => {
          const option = e.target.closest('.decoration-type-option');
          if (option) {
            e.preventDefault();
            e.stopPropagation();
            const input = option.querySelector('input[type="radio"]');
            if (input) {
              const name = input.name;
              document
                .querySelectorAll(`input[name="${name}"]`)
                .forEach((radio) => {
                  radio.checked = false;
                });
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });

        // Decoration Subtype Change
        positionsRoot.addEventListener('change', (e) => {
          if (
            e.target.type === 'radio' &&
            e.target.name &&
            e.target.name.startsWith('decoration-subtype-')
          ) {
            e.preventDefault();
            e.stopPropagation();
            handleSubTypeChange(e.target.dataset.positionId, e.target.value);
          }
        });

        // Decoration Subtype Click
        positionsRoot.addEventListener('click', (e) => {
          const option = e.target.closest('.decoration-subtype-option');
          if (option) {
            e.preventDefault();
            e.stopPropagation();
            const input = option.querySelector('input[type="radio"]');
            if (input) {
              const name = input.name;
              document
                .querySelectorAll(`input[name="${name}"]`)
                .forEach((radio) => {
                  radio.checked = false;
                });
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });

        // Quantity input
        positionsRoot.addEventListener(
          'input',
          debounce((e) => {
            if (e.target.dataset.role === 'qty-input') {
              const currentValue = parseInt(e.target.value) || 1;
              handleQtyChange(e.target.dataset.positionId, currentValue);
            }
          }, 300),
        );

        // Quantity increment and decrement
        positionsRoot.addEventListener('click', (e) => {
          if (
            e.target.dataset.role === 'qty-increment' ||
            e.target.dataset.role === 'qty-decrement'
          ) {
            const positionId = e.target.dataset.positionId;
            const position = addedPositions.find((p) => p.id === positionId);
            if (!position) return;

            const input = document.querySelector(
              `[data-role="qty-input"][data-position-id="${positionId}"]`,
            );
            if (!input) return;

            const currentValue = parseInt(input.value) || position.qty || 1;

            if (e.target.dataset.role === 'qty-increment') {
              input.value = currentValue + 1;
              handleQtyChange(positionId, input.value);
            } else if (
              e.target.dataset.role === 'qty-decrement' &&
              currentValue > 1
            ) {
              input.value = Math.max(1, currentValue - 1);
              handleQtyChange(positionId, input.value);
            }
          }
        });

        // Colors input
        positionsRoot.addEventListener(
          'input',
          debounce((e) => {
            if (e.target.dataset.role === 'colors-input') {
              handleColorsChange(e.target.dataset.positionId, e.target.value);
            }
          }, 300),
        );

        // Colors increment and decrement
        positionsRoot.addEventListener('click', (e) => {
          if (
            e.target.dataset.role === 'colors-increment' ||
            e.target.dataset.role === 'colors-decrement'
          ) {
            const positionId = e.target.dataset.positionId;
            const position = addedPositions.find((p) => p.id === positionId);
            if (!position || position.type !== 'Screen-Print') return;

            const input = document.querySelector(
              `[data-role="colors-input"][data-position-id="${positionId}"]`,
            );
            if (!input) return;

            const currentValue = parseInt(input.value) || 1;

            if (e.target.dataset.role === 'colors-increment') {
              handleColorsChange(positionId, currentValue + 1);
            } else if (e.target.dataset.role === 'colors-decrement') {
              handleColorsChange(positionId, currentValue - 1);
            }
          }
        });

        // Setup Choice Change
        positionsRoot.addEventListener('change', (e) => {
          if (
            e.target.type === 'radio' &&
            e.target.name &&
            e.target.name.startsWith('setup-')
          ) {
            handleSetupTypeChange(e.target.dataset.positionId, e.target.value);
          }
        });

        // Setup Choice Click
        positionsRoot.addEventListener('click', (e) => {
          const option = e.target.closest('.setup-option');
          if (option && !e.target.closest('input')) {
            const input = option.querySelector('input[type="radio"]');
            if (input && !input.checked) {
              input.checked = true;
              handleSetupTypeChange(input.dataset.positionId, input.value);
            }
          }
        });

        // Has Special Material Choice
        positionsRoot.addEventListener('change', (e) => {
          if (
            e.target.type === 'radio' &&
            e.target.name &&
            e.target.name.startsWith('has-special-material-')
          ) {
            handleHasSpecialMaterialChange(
              e.target.dataset.positionId,
              e.target.value,
            );
          }
        });

        // Has Special Material Click
        positionsRoot.addEventListener('click', (e) => {
          const option = e.target.closest('.has-special-material-option');
          if (option && !e.target.closest('input')) {
            const input = option.querySelector('input[type="radio"]');
            if (input && !input.checked) {
              input.checked = true;
              handleHasSpecialMaterialChange(
                input.dataset.positionId,
                input.value,
              );
            }
          }
        });

        // Special Material Type Choice
        positionsRoot.addEventListener('change', (e) => {
          if (
            e.target.type === 'checkbox' &&
            e.target.name &&
            e.target.name.startsWith('special-material-type-')
          ) {
            handleSpecialMaterialTypeChange(
              e.target.dataset.positionId,
              e.target.value,
              e.target.checked,
            );
          }
        });

        // Special Material Type Click
        positionsRoot.addEventListener('click', (e) => {
          const option = e.target.closest('.special-material-type-option');
          if (option && !e.target.closest('input')) {
            const input = option.querySelector('input[type="checkbox"]');
            if (input) {
              input.checked = !input.checked;
              handleSpecialMaterialTypeChange(
                input.dataset.positionId,
                input.value,
                input.checked,
              );
            }
          }
        });

        // Special Service Choice
        positionsRoot.addEventListener('change', (e) => {
          if (
            e.target.type === 'radio' &&
            e.target.name &&
            e.target.name.startsWith('material-')
          ) {
            handleHasSpecialServicesChange(
              e.target.dataset.positionId,
              e.target.value,
            );
          }
        });

        // Special Service Click
        positionsRoot.addEventListener('click', (e) => {
          const option = e.target.closest('.material-option');
          if (option && !e.target.closest('input')) {
            const input = option.querySelector('input[type="radio"]');
            if (input && !input.checked) {
              input.checked = true;
              handleHasSpecialServicesChange(
                input.dataset.positionId,
                input.value,
              );
            }
          }
        });

        // Special Service Change
        positionsRoot.addEventListener('change', (e) => {
          if (e.target.dataset.role === 'service-checkbox') {
            handleServiceChange(
              e.target.dataset.positionId,
              e.target.dataset.service,
            );
          }
        });

        // Special Service Click
        positionsRoot.addEventListener('click', (e) => {
          const serviceOption = e.target.closest('.service-option');
          if (serviceOption && !e.target.closest('input')) {
            const checkbox = serviceOption.querySelector(
              'input[type="checkbox"]',
            );
            if (checkbox) {
              checkbox.checked = !checkbox.checked;
              handleServiceChange(
                checkbox.dataset.positionId,
                checkbox.dataset.service,
              );
            }
          }
        });

        // Size Choice
        positionsRoot.addEventListener('change', (e) => {
          if (
            e.target.type === 'radio' &&
            e.target.name &&
            e.target.name.startsWith('decoration-size-')
          ) {
            handleSizeChange(e.target.dataset.positionId, e.target.value);
          }
        });

        // Size Click
        positionsRoot.addEventListener('click', (e) => {
          const option = e.target.closest('.size-option');
          if (option && !e.target.closest('input')) {
            const input = option.querySelector('input[type="radio"]');
            if (input && !input.checked) {
              input.checked = true;
              handleSizeChange(input.dataset.positionId, input.value);
            }
          }
        });

        // Stitches Change
        positionsRoot.addEventListener('input', (e) => {
          if (e.target.dataset.role === 'stitches-input') {
            handleStitchesChange(e.target.dataset.positionId, e.target.value);
          }
        });

        // Stitches Blur
        positionsRoot.addEventListener('blur', (e) => {
          if (e.target.dataset.role === 'stitches-input') {
            const stitches = parseFloat(e.target.value) || 1;
            e.target.value = stitches;
            handleStitchesChange(e.target.dataset.positionId, stitches);
          }
        });

        document.addEventListener('click', (e) => {
          const viewBtn = e.target.closest('[data-role="view-quote"]');
          if (!viewBtn) return;

          e.preventDefault();
          handleViewQuote();
        });

        // Quote Modal Events
        document.body.addEventListener('click', (e) => {
          const closeBtn = e.target.closest('[data-role="close-quote-modal"]');
          if (closeBtn) {
            const modalId = closeBtn.dataset.modalId;
            if (modalId) {
              closeQuoteModal(modalId);
            }
          }

          const downloadBtn = e.target.closest('[data-role="download-pdf"]');
          if (downloadBtn) {
            handleDownloadPDF();
          }

          const modal = e.target.closest('[data-role="quote-modal"]');
          if (modal && e.target === modal) {
            closeQuoteModal(modal.id);
          }
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            const openModal = document.querySelector(
              '[data-role="quote-modal"]:not(.hidden)',
            );
            if (openModal) {
              closeQuoteModal(openModal.id);
            }
          }
        });
      }
    }

    function bindProductTypeEvents() {
      if (productTypeEventsBound) {
        return;
      }

      productTypeEventsBound = true;
      document.body.addEventListener('change', (e) => {
        if (e.target && e.target.dataset.role === 'product-type-radio') {
          const productType = e.target.value;
          handleProductTypeChange(productType);
        }
      });

      document.body.addEventListener('click', (e) => {
        const productTypeOption = e.target.closest('.product-type-option');
        if (productTypeOption) {
          const radio = productTypeOption.querySelector(
            'input[data-role="product-type-radio"]',
          );
          if (radio && !radio.checked && !radio.disabled) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    function handleProductTypeChange(productType) {
      if (addedPositions.length > 0) {
        return;
      }

      selectedProductType = productType;
      addedPositions = [];
      const calculations = calculate();
      render(calculations);

      bindEvents();
    }

    function handleAddPosition() {
      selectedDecorationIndex = null;
      const maxPositions =
        selectedProductType === 'SUPPLIED_PRODUCTS'
          ? PC2BData.maxPositionsUnbranded
          : PC2BData.maxPositionsConsumable;
      if (addedPositions.length >= maxPositions) {
        return;
      }

      let maxId = 0;
      addedPositions.forEach((position) => {
        const match = position.id.match(/pos-(\d+)/);
        if (match) {
          const idNum = parseInt(match[1], 10);
          if (idNum > maxId) {
            maxId = idNum;
          }
        }
      });
      const id = `pos-${maxId + 1}`;

      let defaultQty = null;
      if (addedPositions.length > 0) {
        const lastPosition = addedPositions[addedPositions.length - 1];
        if (lastPosition.qty && lastPosition.qty > 0) {
          defaultQty = lastPosition.qty;
        }
      }

      const newPosition = initPositionData(null, null, id, defaultQty);

      addedPositions.push(newPosition);
      recalculateAndRender();

      setTimeout(() => {
        if (addedPositions.length > 0) {
          const lastPos = addedPositions[addedPositions.length - 1];
          const card = document.querySelector(
            `[data-position-id="${lastPos.id}"]`,
          );
          if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'start' });
            card.style.animation = 'pulse 0.5s ease-in-out';
            setTimeout(() => {
              card.style.animation = '';
            }, 500);
          }
        }
      }, 100);
    }

    function handleRemovePosition(positionId) {
      if (!confirm('Are you sure you want to remove this position?')) {
        return;
      }

      addedPositions = addedPositions.filter((p) => p.id !== positionId);
      recalculateAndRender();
    }

    function handleCompletePosition(positionId) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      position._collapsed = true;

      recalculateAndRender();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    function handleExpandPosition(positionId) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      position._collapsed = false;
      recalculateAndRender();
    }

    function handleDecorationTypeChange(positionId, decorationName) {
      const position = addedPositions.find((p) => p.id === positionId);
      const decorations =
        selectedProductType === 'SUPPLIED_PRODUCTS'
          ? PC2BData.unbrandedDecorations
          : PC2BData.consumableDecorations;
      selectedDecorationIndex = decorations.findIndex(
        (d) => d.decorationName === decorationName,
      );
      const decorationData =
        selectedDecorationIndex !== -1
          ? decorations[selectedDecorationIndex]
          : null;

      const isSwitching = !!position.type;

      if (isSwitching) {
        const confirmed = confirm(
          'Switching decoration will reset current selections for this position. Continue?',
        );
        if (!confirmed) return;
      }

      let decoration = null;

      if (decorationData.hasChildDecoration) {
        initPositionData(position, decoration, positionId, null);
        position.hasSubtype = true;
        recalculateAndRender();
        return;
      }

      decoration = decorationData.decorations[0];

      const defaultQty = decoration.defaultQty;
      if (!position.qty || position.qty < defaultQty) {
        position.qty = defaultQty;
      }

      initPositionData(position, decoration, positionId, defaultQty);
      position.hasSubtype = false;
      recalculateAndRender();
    }

    function handleSubTypeChange(positionId, decorationName) {
      const position = addedPositions.find((p) => p.id === positionId);
      const decorations =
        selectedProductType === 'SUPPLIED_PRODUCTS'
          ? PC2BData.unbrandedDecorations
          : PC2BData.consumableDecorations;
      const decoration = decorations[selectedDecorationIndex].decorations.find(
        (d) => d.name === decorationName,
      );

      if (!position || !decoration) return;

      const defaultQty = decoration.defaultQty;
      if (!position.qty || position.qty < defaultQty) {
        position.qty = defaultQty;
      }

      initPositionData(position, decoration, positionId, defaultQty);
      position.subType = decorationName;
      recalculateAndRender();
    }

    function handleQtyChange(positionId, value) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      const decoration = getDecorationDataById(position.decorationId);

      const qty = parseInt(value, 10);

      if (qty < decoration.defaultQty && decoration.moqLogic === 'Logic 2') {
        if (decoration.template === 'Power-Sticker') {
          position.moqSurcharge =
            decoration.defaultQty * decoration.prices[0].prices[position.size];
        } else {
          position.moqSurcharge = decoration.moqSurcharge;
        }
      } else {
        position.moqSurcharge = 0;
      }

      if (!qty || qty < 1) {
        position.qty = 1;
        recalculateAndRender();
      } else {
        position.qty = qty;
        recalculateAndRender();
      }
    }

    // PC2A only: Blank Products quantity handler (qty may be 0 = not ordered).
    function handleBlankQtyChange(blankId, value) {
      const entry = blankProducts.find((b) => b.id === blankId);
      if (!entry) return;
      const qty = parseInt(value, 10);
      entry.qty = !qty || qty < 0 ? 0 : qty;
      // No re-render: the quote reads blankProducts on open. Re-rendering here
      // would recreate the input element and reset the caret to the start.
    }

    function handleColorsChange(positionId, value) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      const decoration = getDecorationDataById(position.decorationId);

      const numColors = parseInt(value, 10);
      const input = document.querySelector(
        `[data-role="colors-input"][data-position-id="${positionId}"]`,
      );
      const field = input?.closest('.colors-field');

      const min = 1;
      const max = decoration.maxColour;

      if (numColors < min) {
        position.numColors = min;
        input.value = min;
      } else if (numColors > max) {
        position.numColors = max;
        input.value = max;
      } else {
        position.numColors = numColors;
      }
      recalculateAndRender();
    }

    function handleSetupTypeChange(positionId, setupType) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      position.setupType = setupType;
      recalculateAndRender();
    }

    function handleSizeChange(positionId, size) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      position.size = size;
      recalculateAndRender();
    }

    function handleStitchesChange(positionId, stitches) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      const stitchesNum = parseFloat(stitches) || 1;
      if (stitchesNum <= 0) {
        return;
      }

      position.stitches = stitchesNum;
      recalculateAndRender();
    }

    function handleHasSpecialMaterialChange(positionId, hasSpecialMaterial) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      const hasMat =
        hasSpecialMaterial === 'true' || hasSpecialMaterial === true;
      position.hasSpecialMaterial = hasMat;

      if (!hasMat) {
        position.specialMaterialType = [];
      }

      recalculateAndRender();
    }

    function handleSpecialMaterialTypeChange(
      positionId,
      specialMaterialType,
      checked,
    ) {
      const position = addedPositions.find((p) => p.id === positionId);
      if (!position) return;

      if (checked) {
        if (!position.specialMaterialType.includes(specialMaterialType)) {
          position.specialMaterialType.push(specialMaterialType);
        }
      } else {
        position.specialMaterialType = position.specialMaterialType.filter(
          (v) => v !== specialMaterialType,
        );
      }

      recalculateAndRender();
    }

    function handleHasSpecialServicesChange(positionId, material) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      position.material = material;
      if (material === 'STANDARD') {
        position.services = [];
      }

      recalculateAndRender();
    }

    function handleServiceChange(positionId, service) {
      const position = addedPositions.find((p) => p.id === positionId);

      if (!position) return;

      if (position.services.includes(service)) {
        position.services = position.services.filter((s) => s !== service);
      } else {
        position.services.push(service);
      }

      recalculateAndRender();
    }

    function handleViewQuote() {
      const calculations = calculate();

      const existingModal = document.querySelector(
        '[data-role="quote-modal"]:not(.hidden)',
      );
      if (existingModal) {
        closeQuoteModal(existingModal.id);
      }

      const modalHtml = renderQuoteModal(calculations);
      document.body.insertAdjacentHTML('beforeend', modalHtml);

      setTimeout(() => {
        const modal = document.getElementById('quote-modal');
        if (modal) {
          modal.classList.remove('hidden');
          modal.classList.add('flex');
          document.body.style.overflow = 'hidden';

          const closeButtons = modal.querySelectorAll(
            '[data-role="close-quote-modal"]',
          );
          closeButtons.forEach((btn) => {
            btn.addEventListener('click', (e) => {
              e.stopPropagation();
              const modalId = btn.dataset.modalId || modal.id;
              closeQuoteModal(modalId);
            });
          });

          modal.addEventListener('click', (e) => {
            if (e.target === modal) {
              closeQuoteModal(modal.id);
            }
          });
        }
      }, 10);
    }

    function closeQuoteModal(modalId) {
      const modal = document.getElementById(modalId);
      if (modal) {
        modal.classList.add('hidden');
        modal.classList.remove('flex');
        document.body.style.overflow = '';
        setTimeout(() => {
          modal.remove();
        }, 300);
      }
    }

    function handleDownloadPDF() {
      const original = document.getElementById('quote');

      const opt = {
        margin: 10,
        filename: 'quote.pdf',
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };

      html2pdf().set(opt).from(original).save();
    }
    window.handleDownloadPDF = handleDownloadPDF;

    function formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) {
        return '$0.00';
      }
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }

    function debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    function getDecorationDataById(decorationId) {
      const decorations =
        selectedProductType === 'SUPPLIED_PRODUCTS'
          ? PC2BData.unbrandedDecorations
          : PC2BData.consumableDecorations;
      let decoration = null;
      for (const item of decorations) {
        for (const d of item.decorations) {
          if (d.id === decorationId) {
            decoration = d;
            break;
          }
        }
      }
      return decoration;
    }

    function initPositionData(position, decoration, id, defaultQty) {
      if (position) {
        position.decorationId = decoration?.id;
        position.name = decoration?.name;
        position.code = decoration?.code;
        position.type = decoration?.template;
        position.qty = defaultQty;
        position.moqQty = decoration?.defaultQty;
        position.moqSurcharge = 0;
        position.numColors = 1;
        position.subType = null;
        position.setupType = 'New';
        position.hasSpecialMaterial = false;
        position.multiplierLimit = decoration?.multiplierLimit;
        position.specialMaterialType = [];
        position.material = 'STANDARD';
        position.services = [];
        position.pricingFile = decoration?.pricingFile;

        if (decoration?.template === 'Embroidery') {
          position.size = null;
          position.stitches = 1;
        } else if (
          decoration?.template == 'Power-Print' ||
          decoration?.template == 'Power-Sticker'
        ) {
          position.size = Object.keys(decoration.prices[0].prices)[0];
        } else {
          position.size = null;
        }
      } else {
        const newPosition = {
          decorationId: null,
          id: id,
          name: '',
          code: '',
          type: null,
          qty: defaultQty,
          moqQty: null,
          moqSurcharge: 0,
          numColors: 1,
          size: null,
          stitches: 1,
          subType: null,
          setupType: 'New',
          hasSpecialMaterial: false,
          multiplierLimit: 100,
          specialMaterialType: [],
          material: 'STANDARD',
          services: [],
          subtotal: 0,
          pricingFile: null,
          _collapsed: false,
        };
        return newPosition;
      }
    }

    function findProductPriceByQty(qty, pricing) {
      if (!Array.isArray(pricing) || pricing.length === 0) {
        return null;
      }

      const tiers = pricing
        .map((item) => ({
          minQty: parseInt(item.qty, 10),
          price: Number(item.price),
        }))
        .filter((item) => !Number.isNaN(item.minQty))
        .sort((a, b) => a.minQty - b.minQty);

      if (tiers.length === 0) {
        return null;
      }

      let matchedPrice = tiers[0].price;

      for (const tier of tiers) {
        if (qty >= tier.minQty) {
          matchedPrice = tier.price;
        } else {
          break;
        }
      }

      return matchedPrice;
    }

    function round2(num) {
      return Math.round(num * 100) / 100;
    }
  }
});
